import css from "./methods.module.scss";
import { Layout } from "../../../componentsV1/layout";
import Logo from "../../../assets/v1/logo.png";

export const Methods = () => {

  const methods = [
    {
      title: 'Protein Chemistry Methods',
      heading: 'Built to Preserve Signal Integrity',
      description: 'Identifyn® protein chemistry workflows are engineered to preserve antibody specificity, fluorophore performance, structural integrity, and imaging compatibility across advanced microscopy applications.',
      features: ['Conjugation Efficiency', 'Fluorophore Stability', `Signal-to-background Performace`, `Lot-to-lot consistency`, `Western Blot Compatibility`, `Super-Resolution Compatability`, `Direct Conjugation Performance`, `Resolution-dependent Fluorophore Behaviour`]
    },
    {
      title: 'Optical Microscopy Methods',
      heading: 'Imaging Methods Designed for Transparency',
      description: 'Identifyn® microscopy workflows document how each reagent performs under real acquisition conditions rather than relying on isolated representative images.',
      features: ['Sample Preparation', 'Fixation Methodology', 'Laser Power Settings', 'Exposure Settings', 'Detector Configuration', 'Channel Metadata', 'Localization Analysis', 'Line-Profile Analysis', 'Histogram Measurements', 'Control Datasets', 'Structural Comparison', 'Metadata Transparency']
    }
  ]

  const microscopy = [
    {
      title: 'Conventional Fluorescence Microscopy',
      description: 'Confocal microscopy represents the upper boundry of conventional fluorescence microscopy validation within the Identifyn® framework.',
      features: ['Western Blot', 'Widefield', 'Widefield SIM', 'Confocal']
    },
    {
      title: 'Super-Resolution Microscopy',
      description: 'Within the Identifyn® framework, the transition into super-resolution microscopy begins at Airyscan. STORM represents true single-molecule localization microscopy (SMLM).',
      features: ['Airyscan', 'Lattice SIM', 'Lattice SIM Squared', 'STORM']
    }
  ]

  const whyMethods = ['Specificity and Localization Behavior', 'Localization-Dependent Imaging Performance', 'Fluorophore Stability and Structural Preservation', 'Background Behavior and Signal Continuity', 'Super-Resolution Compatibility', 'Reproducibility Across Modalities']

  return (
    <Layout>
      <div className={css.heroContainer}>
        <div className={css.heroContent}>
          <img src={Logo} alt="logo" className={css.heroLogo} />
          <div className={css.heroText}>
            <h1>One Standard Across Protein Chemistry, Fluorescence, Imaging, and Super-Resolution Validation</h1>
            <div className={css.line} />
            <p>Identifyn® Methods unify protein chemistry, fluorescent conjugation, antibody validation, and optical microscopy into one integrated scientific framework engineered for transparency, reproducibility, and resolution-dependent imaging validation.</p>
          </div>
        </div>
      </div>
      <div className={css.container}>
        {/* first section */}
        <div className={css.column}>
          <h5 className={css.specialText}>Validation Framework</h5>
          <div className={css.row}>
            <h2 className={css.heading}>Does biological signal integrity remain consistent as optical resolution increases?</h2>
            <div className={css.column}>
              <p>Every Identifyn® reagent is evaluated across progressively higher-resolution imaging systems to determine whether localization behavior, structural continuity, and fluorescence characteristics remain biologically consistent.</p>
              <p>Our methods framework integrates protein chemistry validation, fluorescence microscopy, super-resolution microscopy, and single-molecule localization microscopy into one connected scientific workflow.</p>
            </div>
          </div>
        </div>
        {/* second section */}
        <div className={css.row}>
          {methods.map((method, index) => (
            <div className={css.card} key={index}>
              <div className={css.column}>
                <h5 className={css.specialText}>{method.title}</h5>
                <h2 className={css.heading}>{method.heading}</h2>
                <p>{method.description}</p>
                <div className={css.twoColumn}>
                  {method.features.map((feature, index) => (
                    <div className={css.pillCard} key={index}>
                      <p>{feature}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
          }
        </div>
        {/* third section */}
        <div className={`${css.column} ${css.centerText} ${css.smallContainer}`}>
          <h5 className={css.specialText}>The Identifyn® Stepdown Framework</h5>
          <h2 className={css.heading}>Conventional Fluorescence to Single-Molecule Localization Microscopy</h2>
          <p>Identifyn® validates imaging reagents across progressively higher-resolution optical systems to determine whether biological localization, structural integrity, and fluorescence behavior remain scientifically consistent as imaging resolution increases.</p>
        </div>
        {/* fourth section */}
        <div className={css.row}>
          {microscopy.map((item, index) => (
            <div className={css.card} key={index}>
              <div className={css.column}>
                <h5 className={css.specialText}>{item.title}</h5>
                <div className={css.singleColumn}>
                  {item.features.map((feature, index) => (
                    <div className={css.pillCard} key={index}>
                      <p>{feature}</p>
                    </div>
                  ))}
                </div>
                <p>{item.description}</p>
              </div>
            </div>
          ))
          }
        </div>
        {/* fifth section */}
        <div className={css.card}>
          <div className={css.row}>
            <div className={`${css.column} ${css.flex}`}>
              <h5 className={css.specialText}>Single-Molecule Localization Microscopy</h5>
              <h2 className={css.heading}>STORM Validation</h2>
              <p>STORM (Stochastic Optical Reconstruction Microscopy) represents the hightest-resolution imaging modality currently used within the Identifyn® Stepdown Framework.</p>
              <p>Unlike diffraction-limited or structured illumination systems, STORM reconstructs nanoscale biological structures through the temporal localization of individual fluorescent emission events.</p>
            </div>
            <div className={`${css.singleColumn} ${css.flex}`}>
              <div className={css.pillCard}>
                <p>Localization Precision</p>
              </div>
              <div className={css.pillCard}>
                <p>Nanoscale Structural Organization</p>
              </div>
              <div className={css.pillCard}>
                <p>Signal Continuity</p>
              </div>
              <div className={css.pillCard}>
                <p>Fluorophore Performance Under Single-Molecule Conditions</p>
              </div>
              <div className={css.pillCard}>
                <p>Resolution-Dependent Localization Preservation</p>
              </div>
            </div>
          </div>
        </div>
        {/* sixth section */}
        <div className={`${css.column} ${css.centerText} ${css.smallContainer}`}>
          <h5 className={css.specialText}>Why Identifyn® Methods Matter</h5>
          <h2 className={css.heading}>Most antibody pages show a single image. <br/> Identifyn® documents the methodology behind the image</h2>
          <div className={css.threeColumn}>
            {whyMethods.map((method, index) => (
              <div className={css.pillCard} key={index}>
                <p>{method}</p>
              </div>
            ))}
          </div>
        </div>
        {/* seventh section */}
        <div className={`${css.column} ${css.centerText} ${css.smallContainer} ${css.card}`}>
          <h5 className={css.specialText}>One Integrated Imaging Standard</h5>
          <h2 className={css.heading}>This is not representative imaging. This is systematic optical validation.</h2>
          <p>From western blot characterization to nanoscale single-molecule localization microscopy, Identifyn® establishes a continuous evidence-based framework designed to show researchers exactly how imaging reagents perform under real experimental conditions.</p>
          <h4 className={css.italic}>Because the closer you look, the more the science matters.</h4>
        </div>
      </div>
    </Layout>
  );
}